import { defineStore } from 'pinia'
import { LOGOUT } from '@/data/url'
import apiClient from '@/services/apiClient'
import InitializerService from '@/services/initializer.service'
import { useDropdownsStore } from '@/stores/dropdownsStore'
import { useProfileStore } from '@/stores/profileStore'
import { useSessionStore } from '@/stores/sessionStore'

const defaultUser = () => ({
    id: '',
    username: 'NONE',
    first_name: '',
    last_name: '',
    email: '',
    is_authenticated: false,
    is_active: false,
    is_staff: false,
    is_superuser: false,
    dashboard_permissions: [],
})

let initPromise = null

export const useUserStore = defineStore('user', {
    state: () => ({
        ...defaultUser(),
        initialized: false,
        initializing: false,
        redirectAfterLoginPath: null,
    }),
    getters: {
        isAuthenticated: (state) => Boolean(state.is_authenticated),
        isActive: (state) => Boolean(state.is_active),
        isStaff: (state) => Boolean(state.is_staff),
        isSuperuser: (state) => Boolean(state.is_superuser),
        dashboardPermissions: (state) => state.dashboard_permissions || [],
        getUsername: (state) => state.username || '',
        getFirstName: (state) => state.first_name || '',
        getLastName: (state) => state.last_name || '',
        getEmail: (state) => state.email || '',
        getId: (state) => state.id,
        getUser: (state) => state,
        getRedirectAfterLoginPath: (state) => state.redirectAfterLoginPath,
    },
    actions: {
        setRedirectAfterLoginPath(path) {
            this.redirectAfterLoginPath = path
        },
        resetUser() {
            Object.assign(this, defaultUser())
            this.initialized = false
            this.initializing = false
        },
        setUser(payload = {}) {
            const permissions =
                payload.dashboard_permissions ??
                payload.permissions ??
                payload.search_parts_permissions ??
                []

            Object.assign(this, {
                ...defaultUser(),
                ...payload,
                is_authenticated:
                    typeof payload.is_authenticated === 'boolean'
                        ? payload.is_authenticated
                        : Boolean(payload.id || payload.username),
                dashboard_permissions: Array.isArray(permissions) ? permissions : [],
            })
        },
        setDashboardPermissions(permissions = []) {
            this.dashboard_permissions = Array.isArray(permissions) ? permissions : []
        },
        async initializeSession() {
            if (this.initialized) {
                return true
            }
            if (initPromise) {
                return initPromise
            }

            this.initializing = true
            initPromise = (async () => {
                try {
                    const response = await InitializerService.initialize()
                    if (!response?.user) {
                        return false
                    }

                    this.setUser({ ...response.user, is_authenticated: true })
                    this.setDashboardPermissions(response.search_parts_permissions || [])

                    try {
                        const dropdownsStore = useDropdownsStore()
                        dropdownsStore.setDropdowns(response.dropdowns || {})
                    } catch (error) {
                        console.error('Failed to sync dropdowns to Pinia store', error)
                    }

                    try {
                        const sessionStore = useSessionStore()
                        sessionStore.setSession(response.session_expiry || '')
                    } catch (error) {
                        console.error('Failed to sync session to Pinia store', error)
                    }

                    try {
                        const profileStore = useProfileStore()
                        profileStore.setPersonalData({
                            username: response.user.username || 'NONE',
                            email: response.user.email || 'NONE',
                            first_name: response.user.first_name || 'NONE',
                            last_name: response.user.last_name || 'NONE',
                        })
                    } catch (error) {
                        console.error('Failed to sync profile data to Pinia store', error)
                    }

                    this.initialized = true
                    return true
                } catch (error) {
                    console.error('Failed to initialize session', error)
                    return false
                } finally {
                    this.initializing = false
                    initPromise = null
                }
            })()

            return initPromise
        },
        async doLogout() {
            await apiClient.post(LOGOUT, {}, { withCredentials: true })

            setTimeout(() => {
                window.location.reload()
            }, 500)
        },
    },
})
